import { createContext, useContext, useState, useCallback } from "react";

const DICT = {
  en: {
    "nav.how": "How it works",
    "nav.engine": "Engine",
    "nav.pricing": "Pricing",
    "nav.resources": "Resources",
    "nav.open": "Open Workspace",
    "hero.overline": "UCP 600 · Documentary Credit Pre-Check",
    "hero.title1": "Catch LC Discrepancies in Seconds,",
    "hero.title2": "Before the Bank Does.",
    "hero.demo.tag": "Live discrepancy scan",
    "guarantee.badge": "Audit Fee Guarantee",
    "guarantee.body": "If LC Shield misses a discrepancy that your bank cites in a refusal notice, and you send us that notice within 30 days of the refusal, we refund the audit fee you paid for that presentation.",
    "guarantee.micro": "Free audits and sample reports have no audit fee and are not covered by the refund.",
    "guarantee.readTerms": "Read guarantee terms",
    "ws.sampleDownload": "Download Sample Executive PDF Audit Report",
    "hero.sub": "Typically under 30 seconds — automated UCP 600 & ISBP 821 (2023) pre-audit for exporters, freight forwarders and trade consultants. Flag hidden discrepancy traps before presenting to issuing banks.",
    "hero.cta": "Check a presentation free",
    "hero.pricing": "View pricing",
    "hero.ctaPrimary": "Try a free audit",
    "hero.ctaSample": "See a sample audit (free, no credit used)",
    "hero.noCard": "No account or credit card required. 1 free real audit; the sample is always free.",
    "hero.badge1": "In-memory processing · BYO-Key",
    "hero.badge2": "UCP 600 & ISBP 821 (2023)",
    "hero.badge3": "1-click PDF / CSV",
    "pricing.overline": "Pricing",
    "pricing.title": "One clean set pays for itself",
    "pricing.sub": "",
    "pricing.billedUsd": "Billed in USD",
    "pricing.buyLifetime": "Buy lifetime",
    "pricing.subscribe": "Subscribe",
    "pricing.perMonth": "mo",
    "pricing.footnote": "Try the workspace before you buy.",
    "work.overline": "The workflow",
    "work.title": "Three steps to a bank-ready set",
    "work.s1t": "1 · Load the credit & documents",
    "work.s1d": "Paste the LC terms and drop in your invoice, bill of lading, packing list and insurance — or upload PDFs. Uploaded documents are not retained after analysis.",
    "work.s2t": "2 · Run the discrepancy check",
    "work.s2d": "The engine cross-references every field against the credit and UCP 600 practice, then scores presentation readiness.",
    "work.s3t": "3 · Fix, then export",
    "work.s3d": "Get a ranked discrepancy list with UCP article references and suggested fixes. Export an executive PDF or CSV for your file.",
    "stat.v1": "of first presentations are refused, ICC estimates (ICC Technical Advisory Briefing No. 3, 2022)",
    "stat.v2": "typical bank discrepancy fee (varies by bank), per presentation",
    "stat.v3": "typical payment delay when documents bounce back (our estimate)",
    "feat.overline": "Zero-friction engine",
    "feat.title": "Built for documentary teams who can't afford a bounce",
    "feat.c1t": "UCP 600 discrepancy engine",
    "feat.c1d": "Amount & tolerance, goods description, latest-shipment & expiry dates, presentation period, originals & copies, endorsements, partial-shipment and transhipment rules — each finding cites the relevant article.",
    "feat.c2t": "Your key, your documents",
    "feat.c2d": "Bring your own Anthropic key. Uploaded documents are not retained after analysis. Firm-plan audit history and your local browser storage may keep audit results, never the document files themselves.",
    "feat.c3t": "Readiness score",
    "feat.c3d": "A single 0–100 confidence score plus a live token-cost estimator so you always know spend before you run.",
    "feat.c4t": "Built for documentary credit work",
    "feat.c4d": "Made for exporters, importers and freight forwarders who prepare and present documents under documentary credits.",
    "sbx.overline": "Live sandbox",
    "sbx.title": "See a real discrepancy scan in seconds",
    "sbx.sub": "No signup, no upload. Run our engine on a sample SWIFT MT700 credit and preview the findings instantly.",
    "sbx.btn": "Try with Sample MT700 LC (Click to Test)",
    "sbx.scanning": "Cross-referencing UCP 600 rules…",
    "sbx.steps": ["Amount & tolerance", "Shipment & expiry dates", "Insurance & endorsements"],
    "sbx.header": "Not presentation-ready — 6 discrepancies found",
    "sbx.readiness": "Readiness 18 / 100",
    "sbx.critical": "Critical",
    "sbx.lcReq": "LC requires:",
    "sbx.docShows": "Document shows:",
    "sbx.revealed": [
      { "title": "Credit Amount Exceeded", "doc": "Commercial Invoice", "lc": "USD 50,000.00 (tolerance +/- 5% · max USD 52,500)", "val": "USD 54,000.00", "ref": "LC field 39A; UCP 600 Art. 18(b)" },
      { "title": "Latest Shipment Date Missed", "doc": "Ocean Bill of Lading", "lc": "On board on or before 2027-01-15", "val": "Shipped on board 2027-01-20", "ref": "LC field 44C; UCP 600 Art. 20(a)(ii)" }
    ],
    "sbx.locked": ["Insurance Certificate not presented (46A)", "Packing List not presented (46A)", "Invoice: 2 of 3 originals presented", "Invoice description omits \"100%\" (45A)"],
    "sbx.unlock": "Unlock remaining 4 risk findings & Amend Letter",
    "sbx.startTrial": "Unlock the full report",
    "roi.overline": "Run the numbers",
    "roi.title": "What one bounced presentation really costs you",
    "roi.lcsLabel": "Monthly LCs Processed",
    "roi.valLabel": "Average LC Value",
    "roi.riskLabel": "Est. annual discrepancy risk avoided",
    "roi.hoursLabel": "Hours saved every month",
    "roi.hrs": "hrs",
    "roi.planLabel": "LC Shield Professional plan",
    "roi.roiLabel": "Estimated ROI",
    "roi.cta": "Start protecting these numbers",
    "roi.perMo": "/mo",
    "roi.note1": "The monthly LC count and average LC value are adjustable; the other figures are illustrative (not ICC data): a first-presentation discrepancy rate, a typical bank fee of ",
    "roi.note2": " (typically USD 50-150), and a 1.5% capital-delay cost. Not a guarantee.",
    "cmp.overline": "The switch",
    "cmp.title": "Why exporters switch from manual checking to LC Shield",
    "cmp.hDim": "Dimension",
    "cmp.hManual": "Manual bank / in-house",
    "cmp.hShield": "LC Shield AI defense",
    "cmp.rows": [
      { "dim": "Turnaround Time", "manual": "Hours to days, depending on team and workload", "shield": "Typically under 30 seconds" },
      { "dim": "UCP 600 / ISBP Rule Mapping", "manual": "Requires senior trade specialist", "shield": "Instant automatic clause search" },
      { "dim": "Resolution Output", "manual": "Manual drafting", "shield": "Draft amendment request for your issuing bank" },
      { "dim": "Cost Per LC Review", "manual": "Internal staff time", "shield": "From $15 per audit (Single Pass); lower per audit on Pro and Firm plans" }
    ],
    "plan.single.name": "Single Audit Pass",
    "plan.single.badge": "Pay-As-You-Go",
    "plan.single.tag": "One-time check for urgent L/C presentations. Zero setup, no subscription.",
    "plan.single.quota": "1 Full L/C Audit Pass",
    "plan.single.cta": "Buy Single Pass",
    "plan.single.features": [
      "1 Full UCP 600 & ISBP 821 (2023) discrepancy scan",
      "SWIFT MT700 auto-mapper + amendment drafter",
      "Executive PDF + CSV export",
      "Built-in Claude Sonnet AI – zero setup required",
    ],
    "plan.professional.name": "Professional",
    "plan.professional.badge": "Individual",
    "plan.professional.tag": "Managed AI engine built in – zero API setup needed.",
    "plan.professional.quota": "30 Full Audits / mo",
    "plan.professional.quotaNote": "Valued at $450 if bought individually",
    "plan.professional.cta": "Subscribe Pro",
    "plan.professional.features": [
      "30 Managed L/C Presentation Checks / mo",
      "Full UCP 600 / ISBP 821 (2023) discrepancy engine",
      "SWIFT MT700 auto-mapper + amendment drafter",
      "Executive PDF + CSV exports",
      "Priority email support",
    ],
    "plan.firm.name": "Firm / Agency",
    "plan.firm.badge": "Team Collaboration",
    "plan.firm.tag": "Built for multi-member collaboration and high-volume document review.",
    "plan.firm.quota": "100 Shared Audits / mo",
    "plan.firm.quotaNote": "Up to 8 Team Seats",
    "plan.firm.cta": "Subscribe Firm",
    "plan.firm.features": [
      "100 Shared Managed Presentation Checks / mo",
      "Up to 8 team seats (shared team license)",
      "Shared team discrepancy history & audit logs",
      "Batch document processing",
      "Priority email support & team onboarding",
    ],
    "plan.solo.name": "Solo Lifetime",
    "plan.solo.badge": "Lifetime Access",
    "plan.solo.tag": "One-time purchase, lifetime UI access. You pay your own API token fees directly to Anthropic.",
    "plan.solo.quota": "Unlimited Audits (BYO Anthropic API Key)",
    "plan.solo.cta": "Buy Lifetime",
    "plan.solo.features": [
      "Unlimited presentations – BYO Anthropic API Key",
      "Full UCP 600 / ISBP 821 (2023) discrepancy engine",
      "SWIFT MT700 auto-mapper + amendment drafter",
      "Executive PDF + CSV exports",
      "Lifetime feature updates included",
    ],
    "pricing.quotaLabel": "Included",
    "pricing.starting": "Starting…",
    "pricing.oneTime": "one-time",
    "pricing.agreePrefix": "By completing payment, you accept our",
    "agree.terms": "Terms of Service",
    "agree.and": "and",
    "agree.privacy": "Privacy Policy",
    "foot.terms": "Terms of Service",
    "foot.resources": "Resources",
    "foot.privacy": "Privacy Policy",
    "foot.about": "About Us",
    "foot.contact": "Contact",
    "foot.security": "Security",
    "foot.copy": "LC Shield (DZent Limited). All rights reserved.",
    "mobile.startScan": "Try a free audit",
    "ws.workspace": "Workspace",
    "ws.zeroStorage": "In-Memory Processing",
    "ws.perRun": "/ run",
    "ws.testing": "Testing…",
    "ws.mapping": "Mapping…",
    "ws.upload": "Upload",
    "ws.sampleMt700": "Sample MT700",
    "ws.byoNote": "Your key is used only for this request and is not saved on our servers. Model:",
    "apikey.trigger": "API Key Setup Guide (30s) →",
    "apikey.howtoLink": "[ How to get? ]",
    "apikey.title": "How to Get Your Anthropic API Key",
    "apikey.stepLabel": "Step",
    "apikey.step1": "Visit Anthropic Console (console.anthropic.com) and sign up for free.",
    "apikey.step2": "Go to the \"API Keys\" section, click \"Create Key\", and copy your secret key.",
    "apikey.step3": "Paste the key into LC Shield settings to enjoy unlimited AI LC pre-audits!",
    "apikey.cost": "By using your own key, actual API cost per LC audit is as low as ~$0.02 USD (approx. $0.16 HKD), giving you full cost control.",
    "pay.cancelTitle": "Checkout cancelled",
    "pay.cancelSub": "No charge was made. You can still try the workspace in demo mode.",
    "pay.pricingBtn": "Pricing",
    "pay.workspaceBtn": "Workspace",
    "ws.managedUnlocked": "Unlocked - managed analyses on {model} (plan quota applies).",
    "ws.managedRunning": "Running on the managed {model} engine. {free}",
    "ws.freeUsed": "Free check used — unlock to continue.",
    "ws.freeIncluded": "{n} free check included.",
    "ws.wsAgree": "By running a check, you agree to our",
    "ws.loadingTitle": "Cross-referencing UCP 600…",
    "ws.loadSteps": ["Amount & tolerance", "Shipment & expiry dates", "Goods description", "Originals & endorsements"],
    "ws.lcRequires": "LC requires:",
    "ws.docShows": "Document shows:",
    "ws.fix": "Fix:",
    "ws.checksPassed": "Checks passed",
    "ws.sev.critical": "Critical",
    "ws.sev.warning": "Warning",
    "ws.sev.info": "Note",
    "ws.phKey": "sk-ant-… or sk-… (your key, not saved on our servers)",
    "ws.phSwift": ":20: Credit No…  :31D: Expiry…  :32B: Currency/Amount…  :44C: Latest shipment…  :45A: Goods…  :46A: Documents required…",
    "ws.phManual": "Paste the documentary credit terms: amount & tolerance, goods, Incoterms, latest shipment & expiry dates, presentation period, required documents…",
    "ws.phDoc": "Paste the document text, or upload a PDF / text file above…",
    "ws.docTypeMap": {},
    "ws.toast.invite": "Team invite applied.",
    "ws.toast.demoScan": "Running your sample MT700 scan…",
    "ws.toast.singleCredit": "Payment confirmed — 1 audit credit added. Run your L/C check now.",
    "ws.toast.paid": "🎉 Payment verified! Welcome to LC Shield. Your account is unlocked.",
    "ws.toast.needKey": "Enter your Anthropic key or switch to Demo engine.",
    "ws.toast.pasteKey": "Paste your API key first.",
    "ws.toast.connOk": "🟢 Connection Successful! Ready for unlimited checks.",
    "ws.toast.connBad": "🔴 Invalid Key or Quota Exceeded. Please check your key.",
    "ws.toast.loadedChars": "Loaded {n} characters from {file}",
    "ws.toast.readFail": "Could not read that file.",
    "ws.toast.sampleLoaded": "Sample credit & documents loaded.",
    "ws.clearAll": "Clear All",
    "ws.freeBanner.remaining": "1 free real LC audit remaining",
    "ws.credits.left": "Credits: {n}",
    "ws.credits.unlimited": "Credits: Unlimited",
    "ws.credits.title": "Your remaining L/C audit credits (free + purchased single-pass credits)",
    "ws.credits.nudge": "0 Credits · Buy Pass",
    "ws.credits.nudgeTitle": "You're out of audit credits — buy a Single Audit Pass to run another L/C check",
    "ws.credits.monthly": "Audits: {used}/{limit} this mo",
    "ws.credits.monthlyTitle": "Managed AI audits used in this billing cycle",
    "ws.credits.limitReached": "{used}/{limit} · Upgrade",
    "ws.credits.limitTitle": "You've hit your monthly managed-audit limit. Buy a Single Pass or upgrade to keep auditing.",
    "ws.managedMetered": "Managed {model} — {n} of {limit} monthly audits remaining.",
    "ws.managedSolo": "Solo Lifetime — switch to BYO-Key mode to run unlimited audits with your own Anthropic API key.",
    "ws.freeBanner.used": "1 free real LC audit used — Upgrade to continue",
    "ws.freeBanner.upgrade": "Upgrade",
    "priv.title": "Local Privacy",
    "priv.mask.all": "Redact All",
    "priv.autoMask": "Auto-Mask sensitive data on paste",
    "priv.reportBadge": "🔒 Audited on Client-Side Redacted Input (Privacy-First)",
    "priv.redactedCount": "🔒 {n} sensitive fields redacted",
    "ex.redactedBadge": "Audited on Client-Side Redacted Input (Privacy-First)",
    "priv.mask.amounts": "Mask All Amounts",
    "priv.mask.parties": "Mask Parties",
    "priv.mask.accounts": "Mask Account/IBAN",
    "priv.reset": "Reset Original Text",
    "priv.tip": "Masking amounts or parties may reduce the accuracy of amount, tolerance and party checks. LC Shield is a pre-check aid and does not replace your own review.",
    "priv.toast.masked": "Sensitive data redacted locally in your browser.",
    "priv.toast.reset": "Original text restored from local snapshot.",
    "shield.title": "Privacy by design",
    "shield.wsTitle": "In-Memory Processing",
    "shield.b1t": "Client-Side Local Redaction",
    "shield.b1d": "Mask amounts, parties & account numbers right in your browser before submission.",
    "shield.b2t": "Isolated In-Memory Auditing",
    "shield.b2d": "Processed in isolated memory; uploaded documents are not retained after analysis.",
    "shield.b3t": "Zero AI Model Training",
    "shield.b3d": "Your trade data is not saved to our servers after analysis, and is never used to train LLM models. Firm plan audit history is stored only if you opt in.",
    "ent.mnda": "Need custom compliance assurance? Mutual NDAs (MNDA) available for Firm & Enterprise plans upon request.",
    "ws.toast.cleared": "Workspace cleared.",
    "ws.toast.needSwift": "Paste a SWIFT MT700 message first.",
    "ws.toast.mapped": "MT700 mapped into LC terms.",
    "ws.toast.histLoaded": "Loaded from history.",
    "ws.toast.needLc": "Add the Letter of Credit terms.",
    "ws.toast.needDoc": "Add at least one document.",
    "ws.toast.analysisDone": "Analysis complete.",
    "ws.errMap": "Could not map the MT700 message.",
    "ws.errAmendment": "Could not draft the amendment.",
    "ws.errAnalyze": "Analysis failed. Please retry.",
    "md.amendTitle": "LC Amendment Request",
    "md.amendDrafting": "Drafting amendment letter…",
    "md.amendReqChanges": "Requested changes",
    "md.amendFrom": "From:",
    "md.amendTo": "To:",
    "md.copy": "Copy",
    "md.download": "Download .txt",
    "md.copyFull": "Copy full text",
    "md.downloadTxt": "Download .txt",
    "md.downloadPdf": "Download .pdf",
    "md.amendUcpRef": "UCP 600 Reference",
    "md.amendDisclaimer": "DISCLAIMER: This amendment request is a draft prepared with LC Shield's automated assistant under ICC UCP 600 & ISBP 821 (2023) rules. It is non-binding, does not constitute legal advice, and must be reviewed and authorised by the applicant before submission to the issuing bank.",
    "md.amendPdfDone": "PDF downloaded.",
    "md.amendTxtDone": "Text file downloaded.",
    "md.amendExportFailed": "Export failed — please try again.",
    "md.amendSaved": "Saved",
    "md.amendSavedEmpty": "No saved amendment letters yet. Letters you generate are saved here automatically.",
    "md.amendBackCurrent": "Back to current draft",
    "md.bankSection": "Addressee — Issuing Bank",
    "md.bankNamePh": "Issuing bank name",
    "md.bankEmailPh": "Bank email (for the email draft)",
    "md.bankAddressPh": "Issuing bank address",
    "md.bankHint": "Saved on this device and added to the top of the letter in every export and email draft.",
    "md.openEmail": "Open in Email Client",
    "md.amendEmailOpened": "Opening your email client…",
    "md.amendCopied": "Letter copied to clipboard.",
    "md.copyFailed": "Copy failed — select the text and copy manually.",
    "md.histEmpty1": "No saved checks yet.",
    "md.histEmpty2": "Your analyses save here automatically.",
    "md.histScore": "Score {score}/100 · {crit} critical · {docs} docs",
    "md.reload": "Reload",
    "md.delete": "Delete",
    "md.hiwTitle": "1-Minute Quick Start",
    "md.hiwIntro": "Three steps to a bank-ready presentation:",
    "md.hiwStep": "STEP",
    "md.hiwFlow": [
      { "title": "Paste MT700", "body": "Drop in your SWIFT MT700 credit or upload draft PDF documents." },
      { "title": "Run AI Check", "body": "The engine cross-references every field against UCP 600 & ISBP 821 (2023)." },
      { "title": "Download", "body": "Export a bank-ready PDF report, or auto-draft an amendment letter." }
    ],
    "md.hiwByoTitle": "BYO-Key in 60 seconds",
    "md.hiwByoIntro": "Solo (BYO-Key) users bring their own key for unlimited, private checks. Grab one here:",
    "md.hiwNoteAnthropic": "Console → API Keys → Create Key. Copy the sk-ant-… value.",
    "md.hiwByoOutro": "Paste the key into the Engine → BYO-Key field and hit Test Connection.",
    "md.hiwDisclaimer": "LC Shield is an automated document-verification assistant under ICC UCP 600 & ISBP 821 (2023). Output is non-binding and not legal, banking, or financial advice — always confirm with your bank.",
    "md.hiwStartTour": "🚀 Start the hands-on guided walkthrough",
    "tour.skip": "Skip Tour",
    "tour.prev": "Previous",
    "tour.next": "Next",
    "tour.finish": "Finish",
    "tour.step": "Step {n} / {total}",
    "tour.clickHint": "Click the highlighted control to continue",
    "tour.waiting": "Waiting for the highlighted step…",
    "tour.sampleLoaded": "Sample loaded — review the findings below.",
    "tour.steps": [
      { "title": "AI Audit Engine", "body": "Use the included engine or paste your own API key - switch anytime. You have 1 free real LC audit ready to use." },
      { "title": "Privacy First — Local Redaction", "body": "Privacy first: you can redact sensitive amounts or client names before auditing. Masking amounts or parties may reduce the accuracy of amount, tolerance and party checks." },
      { "title": "Try a Sample", "body": "Click here! Auto-load a real export Letter of Credit and Bill of Lading sample — samples are always free and never use your free credit." },
      { "title": "Start AI Audit", "body": "Click here to let the AI compare UCP 600 articles against your documents in seconds." },
      { "title": "Discrepancy Results", "body": "Review the rejection risks and suggested fixes the engine flags." },
      { "title": "Generate Amendment Request", "body": "Click here to draft an amendment request for your review. It must be authorised by the applicant before it goes to the issuing bank." }
    ],
    "md.obSteps": [
      { "title": "Passwordless & Privacy-First", "body": "No Passwords Required: We operate on a privacy-first architecture — your documents are not saved to our servers after analysis. Your subscription is automatically linked to your Stripe checkout email—click your magic link anytime to restore access on any device." },
      { "title": "Load Sample Trade Scenarios", "body": "Select from Textiles, Electronics, or Commodities sample cases to test real UCP 600 discrepancy checks in seconds." },
      { "title": "High-Fidelity PDF & CSV Reports", "body": "Export detailed UCP 600 compliance reports with vector logos, exact article citations, and amendment request drafting." }
    ],
    "md.obDontShow": "Don't show this again",
    "md.obBack": "Back",
    "md.obNext": "Next",
    "md.obStart": "Try a free audit",
    "ex.title": "Presentation Readiness Report",
    "ex.generated": "Generated {date}  ·  Ref {ref}  ·  Engine {model}",
    "ex.ready": "PRESENTATION READY",
    "ex.notReady": "NOT READY — DISCREPANCIES FOUND",
    "ex.score": "Readiness Score",
    "ex.critical": "Critical",
    "ex.warnings": "Warnings",
    "ex.notes": "Notes",
    "ex.discrepancies": "DISCREPANCIES",
    "ex.document": "Document",
    "ex.reference": "Reference",
    "ex.suggestedFix": "Suggested fix",
    "ex.disclaimer": "DISCLAIMER: LC Shield is an automated document verification assistant operating under ICC UCP 600 & ISBP 821 (2023) rules. It provides non-binding workflow assistance and does not constitute legal advice, banking guarantee, or official document acceptance by issuing banks.",
    "ex.csvHeaders": ["Severity", "Title", "Document", "LC Requirement", "Document Value", "UCP Reference", "Suggested Fix"],
    "ex.preparing": "Preparing localized PDF…",
    "ws.title": "Workspace",
    "ws.engine": "Engine",
    "ws.demo": "Included engine",
    "ws.byo": "BYO-Key",
    "ws.lcTerms": "Letter of Credit terms",
    "ws.loadSample": "Load sample",
    "ws.loadScenario": "Load Sample Scenario",
    "ws.scn.textiles": "Textiles & Apparel",
    "ws.scn.textilesSub": "High risk · missing docs, late shipment, amount breach",
    "ws.scn.electronics": "Electronics & Machinery",
    "ws.scn.electronicsSub": "Clean presentation · fully complying set",
    "ws.scn.commodities": "Commodities & Agro",
    "ws.scn.commoditiesSub": "Multi-doc · origin & bill of exchange mismatch",
    "ws.pasteManual": "Manual / Paste",
    "ws.pasteSwift": "Paste SWIFT MT700",
    "ws.mapSwift": "Map MT700 fields",
    "ws.documents": "Presented documents",
    "ws.addDoc": "Add document",
    "ws.upload": "Upload",
    "ws.run": "Run discrepancy check",
    "ws.running": "Checking presentation…",
    "ws.report": "Discrepancy report",
    "ws.empty": "No presentation checked yet",
    "ws.emptySub": "Load the credit and your documents, then run the discrepancy check. Findings appear here ranked by severity.",
    "ws.ready": "Presentation ready",
    "ws.notReady": "Not ready",
    "ws.history": "History",
    "ws.letters": "Letters",
    "ws.amendment": "Generate Amendment Request",
    "ws.keyVerified": "Key Verified",
    "ws.keyNotSet": "Key Not Set",
    "ws.demoEngine": "Included engine",
    "ws.unlocked": "Unlocked",
    "ws.privacyShield": "Privacy Shield",
    "ws.team": "Team",
    "ws.howItWorks": "How It Works",
    "ws.quickTour": "Guided Tour",
    "chat.title": "Shield Assistant",
    "chat.subtitle": "Ask anything about LC Shield",
    "chat.greeting": "Hi! I'm Shield Assistant 🛡️ Ask me about demos, privacy, or pricing — or tap a suggestion below.",
    "chat.placeholder": "Type your question…",
    "chat.thinking": "Thinking…",
    "chat.pill0": "How to test the MT700 Demo?",
    "chat.pill1": "How is my trade data handled?",
    "chat.pill2": "Pricing & Plans",
    "chat.emailBtn": "Talk to our team",
    "chat.open": "Open support chat",
    "chat.error": "Sorry, I couldn't reply. Please email support@getlcshield.com.",
    "ws.testConnection": "Test Connection",
    "team.title": "Firm Team Seats",
    "team.sub": "Firm plan includes up to 8 seats. Share one invite link — teammates unlock full access instantly, no extra payment.",
    "team.ownerEmail": "Your Firm account email",
    "team.create": "Create / load team",
    "team.inviteLink": "Team invite link",
    "team.copy": "Copy link",
    "team.members": "Members",
    "team.seatsUsed": "seats used",
    "paywall.invite": "Have a team invite code?",
    "paywall.invitePh": "Invite code",
    "paywall.join": "Join",
    "privacy.badge": "Global Privacy Standard: In-Memory Processing — your documents are not saved to our servers after analysis. Some settings and history may be saved in your browser.",
    "disclaimer.global": "DISCLAIMER: LC Shield is an automated document verification assistant operating under ICC UCP 600 & ISBP 821 (2023) rules. It provides non-binding workflow assistance and does not constitute legal advice, banking guarantee, or official document acceptance by issuing banks.",
    "paywall.title": "You've used your free check",
    "paywall.freeTitle": "Protect Your Next L/C Presentation from Bank Fees — typically USD 50-150 per refusal",
    "paywall.freeSub": "Choose 1 Single Pass for an immediate urgent audit, or upgrade to Professional for maximum cost savings.",
    "paywall.trustLine": "No setup · Cancel before the next renewal · Documents not retained after analysis",
    "paywall.recommended": "Recommended",
    "paywall.cta.single": "Buy Single Pass",
    "paywall.cta.pro": "Subscribe Pro",
    "paywall.saveCopy": "Save 55%+ per audit with the Professional Plan.",
    "paywall.moreOptions": "More options",
    "paywall.currency": "Currency",
    "paywall.forByo": "Best for BYO Key",
    "paywall.sub": "Unlock the SWIFT MT700 mapper and the amendment drafter — or bring your own Anthropic key for free.",
    "paywall.viewPlans": "View plans",
    "paywall.byo": "Use my own API key",
    "paywall.already": "Already purchased? Unlock with your email",
    "paywall.unlock": "Unlock",
    "paywall.emailPh": "Email used at checkout",
  },
  "zh-TW": {
    "nav.how": "運作方式",
    "nav.engine": "引擎",
    "nav.pricing": "價格",
    "nav.resources": "資源中心",
    "nav.open": "開啟工作區",
    "hero.overline": "UCP 600 · 全球跟單信用狀助手",
    "hero.title1": "在數秒內揪出信用狀不符點，",
    "hero.title2": "搶在銀行之前。",
    "hero.demo.tag": "即時不符點掃描",
    "guarantee.badge": "稽核費用保證",
    "guarantee.body": "若 LC Shield 漏判某項不符點，而您的銀行於拒付通知中引用該不符點，且您在拒付後 30 天內將該通知寄給我們，我們將退還您就該次交單所支付的稽核費用。",
    "guarantee.micro": "免費稽核與範例報告不涉及稽核費用，不在退款範圍內。",
    "guarantee.readTerms": "查看保證條款",
    "ws.sampleDownload": "下載範例行政級 PDF 稽核報告",
    "hero.sub": "通常不到 30 秒——為全球出口商、物流代理與貿易顧問提供自動化 UCP 600 與 ISBP 821 (2023) 預先稽核，在向開狀行交單前標示隱藏的不符點陷阱。",
    "hero.cta": "免費檢查一次交單",
    "hero.pricing": "查看價格",
    "hero.ctaPrimary": "免費試用一次稽核",
    "hero.ctaSample": "查看稽核範例",
    "hero.noCard": "無需註冊帳號或信用卡 • 免費試用一次稽核",
    "hero.badge1": "記憶體內處理 · 自帶金鑰",
    "hero.badge2": "UCP 600 與 ISBP 821 (2023)",
    "hero.badge3": "一鍵 PDF / CSV",
    "pricing.overline": "價格",
    "pricing.title": "一份乾淨的單據即回本",
    "pricing.sub": "",
    "pricing.billedUsd": "以美元結算",
    "pricing.buyLifetime": "購買終身版",
    "pricing.subscribe": "訂閱",
    "pricing.perMonth": "月",
    "pricing.footnote": "購買前可先試用工作區。",
    "work.overline": "運作流程",
    "work.title": "三步備妥可交單單據",
    "work.s1t": "1 · 載入信用狀與單據",
    "work.s1d": "貼上信用狀條款，放入您的發票、提單、裝箱單與保險單——或上傳 PDF。上傳的單據於分析後不會保留。",
    "work.s2t": "2 · 執行不符點檢查",
    "work.s2d": "引擎將每個欄位與信用狀及 UCP 600 慣例逐項比對，並為交單就緒程度評分。",
    "work.s3t": "3 · 修正後匯出",
    "work.s3d": "取得依嚴重程度排序的不符點清單，附 UCP 條文引用與建議修正，並可匯出主管級 PDF 或 CSV 存檔。",
    "stat.v1": "的首次交單被拒，ICC 估計（ICC 技術諮詢簡報第 3 號，2022）",
    "stat.v2": "常見的銀行不符點費用——每次交單、每個週期",
    "stat.v3": "當單據被退回修正時的付款延誤",
    "feat.overline": "零阻力引擎",
    "feat.title": "為承擔不起退單的團隊而打造",
    "feat.c1t": "UCP 600 不符點引擎",
    "feat.c1d": "金額與寬容度、貨物描述、最遲裝運與到期日、交單期限、正本與副本、背書、分批裝運與轉運規則——每項發現皆引用相關條文。",
    "feat.c2t": "注重隱私的設計",
    "feat.c2d": "自帶 Anthropic 金鑰。上傳的單據於分析後不會保留。Firm 方案稽核歷史與您的本機瀏覽器儲存可能保存稽核結果，但絕不保存單據檔案本身。",
    "feat.c3t": "交單就緒評分",
    "feat.c3d": "單一 0–100 信心評分，並附即時代幣成本估算，讓您在執行前即掌握花費。",
    "feat.c4t": "為全球跨境貿易而建",
    "feat.c4d": "專為全球出口中小企業、貨運承攬商與貿易櫃檯打造，處理跨司法管轄區與多幣別的跟單信用狀。",
    "sbx.overline": "即時沙盒",
    "sbx.title": "數秒內看到真實的不符點掃描",
    "sbx.sub": "無需註冊、無需上傳。以範例 SWIFT MT700 信用狀執行我們的引擎，立即預覽結果。",
    "sbx.btn": "以範例 MT700 信用狀試用（點擊測試）",
    "sbx.scanning": "正在比對 UCP 600 規則…",
    "sbx.steps": ["金額與寬容度", "裝運與到期日", "保險與背書"],
    "sbx.header": "尚未就緒——發現 6 個不符點",
    "sbx.readiness": "就緒度 18 / 100",
    "sbx.critical": "嚴重",
    "sbx.lcReq": "信用狀要求：",
    "sbx.docShows": "單據顯示：",
    "sbx.revealed": [
      { "title": "信用狀金額超出上限", "doc": "商業發票", "lc": "USD 50,000.00（寬容度 +/- 5% · 上限 USD 52,500）", "val": "USD 54,000.00", "ref": "LC field 39A; UCP 600 Art. 18(b)" },
      { "title": "逾越最遲裝運日", "doc": "海運提單", "lc": "須於 2027-01-15（含）前裝船", "val": "裝船日 2027-01-20", "ref": "LC field 44C; UCP 600 Art. 20(a)(ii)" }
    ],
    "sbx.locked": ["未提供保險證明（46A）", "未提供裝箱單（46A）", "發票：3 份正本僅提交 2 份", "發票貨描漏列「100%」（45A）"],
    "sbx.unlock": "解鎖其餘 4 項風險發現與修改函",
    "sbx.startTrial": "解鎖完整報告",
    "roi.overline": "試算數字",
    "roi.title": "一次退單究竟讓您付出多少代價",
    "roi.lcsLabel": "每月處理的信用狀數",
    "roi.valLabel": "平均信用狀金額",
    "roi.riskLabel": "預估每年可避免的不符點風險",
    "roi.hoursLabel": "每月節省的工時",
    "roi.hrs": "小時",
    "roi.planLabel": "LC Shield 專業版方案",
    "roi.roiLabel": "預估投資報酬率",
    "roi.cta": "立即開始守護這些數字",
    "roi.perMo": "/月",
    "roi.note1": "每月信用狀數與平均信用狀金額可自行調整；其餘數字僅供說明（非 ICC 數據）：首次交單不符率、常見銀行費用 ",
    "roi.note2": "（一般為 USD 50-150），以及 1.5% 的資金延遲成本。並非保證。",
    "cmp.overline": "為何轉換",
    "cmp.title": "全球出口商為何從人工核對轉向 LC Shield",
    "cmp.hDim": "項目",
    "cmp.hManual": "人工／銀行內部審核",
    "cmp.hShield": "LC Shield AI 防護",
    "cmp.rows": [
      { "dim": "處理時間", "manual": "4 至 24 小時", "shield": "通常不到 30 秒" },
      { "dim": "UCP 600 / ISBP 條文對應", "manual": "需資深貿易專員", "shield": "即時自動查找條文" },
      { "dim": "解決方案輸出", "manual": "人工草擬", "shield": "修改申請書草擬" },
      { "dim": "每份信用狀審核成本", "manual": "內部人力成本", "shield": "單次稽核 US$15 起；專業版與企業版每次更低" }
    ],
    "plan.single.name": "單次稽核通行證",
    "plan.single.badge": "按次付費",
    "plan.single.tag": "為緊急信用狀交單提供的一次性檢查。零設定、免訂閱。",
    "plan.single.quota": "1 次完整信用狀稽核",
    "plan.single.cta": "購買單次通行證",
    "plan.single.features": [
      "1 次完整 UCP 600 與 ISBP 821 (2023) 不符點掃描",
      "SWIFT MT700 自動對應 + 修改函草擬",
      "專業 PDF + CSV 匯出",
      "內建 Claude Sonnet AI — 零設定",
    ],
    "plan.professional.name": "專業版",
    "plan.professional.badge": "單一出口商",
    "plan.professional.tag": "內建託管 AI 引擎 — 無需任何 API 設定。",
    "plan.professional.quota": "每月 30 次完整稽核",
    "plan.professional.quotaNote": "單買約值 US$450",
    "plan.professional.cta": "訂閱專業版",
    "plan.professional.features": [
      "每月 30 次託管信用狀交單檢查",
      "完整 UCP 600 / ISBP 821 (2023) 不符點引擎",
      "SWIFT MT700 自動對應 + 修改函草擬",
      "專業 PDF + CSV 匯出",
      "優先電子郵件支援",
    ],
    "plan.firm.name": "企業／代理版",
    "plan.firm.badge": "團隊協作",
    "plan.firm.tag": "專為多隊員協作與大批量單據審核而打造。",
    "plan.firm.quota": "每月 100 次共享稽核",
    "plan.firm.quotaNote": "最多 8 個團隊席位",
    "plan.firm.cta": "訂閱企業版",
    "plan.firm.features": [
      "每月 100 次共享託管交單檢查",
      "最多 8 個團隊席位（共享團隊授權）",
      "共享團隊不符點紀錄與稽核日誌",
      "批次單據處理",
      "優先電子郵件支援與團隊導入",
    ],
    "plan.solo.name": "終身單人版",
    "plan.solo.badge": "終身使用權",
    "plan.solo.tag": "一次買斷，永久使用介面。API Token 費用由您直接支付給 Anthropic。",
    "plan.solo.quota": "無限次稽核（自備 Anthropic API Key）",
    "plan.solo.cta": "購買終身版",
    "plan.solo.features": [
      "無限次交單 — 自備 Anthropic API Key",
      "完整 UCP 600 / ISBP 821 (2023) 不符點引擎",
      "SWIFT MT700 自動對應 + 修改函草擬",
      "專業 PDF + CSV 匯出",
      "終身功能更新",
    ],
    "pricing.quotaLabel": "包含",
    "pricing.starting": "啟動中…",
    "pricing.oneTime": "一次性",
    "pricing.agreePrefix": "完成付款即表示您接受我們的",
    "agree.terms": "服務條款",
    "agree.and": "與",
    "agree.privacy": "隱私政策",
    "foot.terms": "服務條款",
    "foot.resources": "資源中心",
    "foot.privacy": "隱私政策",
    "foot.about": "關於我們",
    "foot.contact": "聯絡",
    "foot.security": "安全",
    "foot.copy": "LC Shield（DZent Limited）。版權所有。",
    "mobile.startScan": "免費試用一次稽核",
    "ws.workspace": "工作區",
    "ws.zeroStorage": "記憶體內處理",
    "ws.perRun": "/ 次",
    "ws.testing": "測試中…",
    "ws.mapping": "對應中…",
    "ws.upload": "上傳",
    "ws.sampleMt700": "範例 MT700",
    "ws.byoNote": "您的金鑰僅用於此次請求，不會儲存於我們的伺服器。模型：",
    "apikey.trigger": "API Key 設定指引（30秒）→",
    "apikey.howtoLink": "[ 如何取得？ ]",
    "apikey.title": "如何取得您的 Anthropic API Key？",
    "apikey.stepLabel": "步驟",
    "apikey.step1": "造訪 Anthropic 官網 (console.anthropic.com) 並完成免費註冊。",
    "apikey.step2": "進入 API Keys 頁面，點擊「Create Key」並複製您的專屬金鑰。",
    "apikey.step3": "貼入 LC Shield 設定面板，即可享有無上限 AI 信用狀預審！",
    "apikey.cost": "透過自備 Key，每份信用狀預審的 Anthropic 實際 API 消耗低至 ~$0.02 USD (約 $0.16 HKD)，成本極低且完全由您掌控。",
    "pay.cancelTitle": "付款已取消",
    "pay.cancelSub": "系統未進行任何扣款。您仍可於示範模式中免費體驗工作區。",
    "pay.pricingBtn": "查看方案",
    "pay.workspaceBtn": "前往工作區",
    "ws.managedUnlocked": "已解鎖 - 在 {model} 上進行託管分析（適用方案額度）。",
    "ws.managedRunning": "正在使用託管的 {model} 引擎。{free}",
    "ws.freeUsed": "免費檢查已用完——請解鎖以繼續。",
    "ws.freeIncluded": "包含 {n} 次免費檢查。",
    "ws.wsAgree": "執行檢查即表示您同意我們的",
    "ws.loadingTitle": "正在比對 UCP 600…",
    "ws.loadSteps": ["金額與寬容度", "裝運與到期日", "貨物描述", "正本與背書"],
    "ws.lcRequires": "信用狀要求：",
    "ws.docShows": "單據顯示：",
    "ws.fix": "修正：",
    "ws.checksPassed": "已通過的檢查",
    "ws.sev.critical": "嚴重",
    "ws.sev.warning": "警告",
    "ws.sev.info": "備註",
    "ws.phKey": "sk-ant-… 或 sk-…（您的金鑰，不會儲存於我們的伺服器）",
    "ws.phSwift": ":20: 信用狀號…  :31D: 到期…  :32B: 幣別／金額…  :44C: 最遲裝運…  :45A: 貨物…  :46A: 所需單據…",
    "ws.phManual": "貼上跟單信用狀條款：金額與寬容度、貨物、貿易條件、最遲裝運與到期日、交單期限、所需單據…",
    "ws.phDoc": "貼上單據文字，或於上方上傳 PDF／文字檔…",
    "ws.docTypeMap": {
      "Commercial Invoice": "商業發票",
      "Bill of Lading / Transport Doc": "提單／運輸單據",
      "Packing List": "裝箱單",
      "Insurance Certificate": "保險證明",
      "Certificate of Origin": "產地證明",
      "Bill of Exchange / Draft": "匯票／匯券",
      "Other Document": "其他單據"
    },
    "ws.toast.invite": "已套用團隊邀請。",
    "ws.toast.demoScan": "正在執行您的範例 MT700 掃描…",
    "ws.toast.singleCredit": "付款成功 — 已新增 1 次稽核額度，現在即可執行信用狀檢查。",
    "ws.toast.paid": "🎉 付款已驗證！歡迎使用 LC Shield，您的帳戶已解鎖。",
    "ws.toast.needKey": "請輸入您的 Anthropic 金鑰，或切換至體驗引擎。",
    "ws.toast.pasteKey": "請先貼上您的 API 金鑰。",
    "ws.toast.connOk": "🟢 連線成功！已可進行無限次檢查。",
    "ws.toast.connBad": "🔴 金鑰無效或額度已用盡，請檢查您的金鑰。",
    "ws.toast.loadedChars": "已從 {file} 載入 {n} 個字元",
    "ws.toast.readFail": "無法讀取該檔案。",
    "ws.toast.sampleLoaded": "已載入範例信用狀與單據。",
    "ws.clearAll": "清空內容",
    "ws.freeBanner.remaining": "剩餘 1 次免費真實審單額度",
    "ws.credits.left": "額度：{n}",
    "ws.credits.unlimited": "額度：無限",
    "ws.credits.title": "您剩餘的信用狀稽核額度（免費 + 已購買的單次通行證額度）",
    "ws.credits.nudge": "0 額度 · 購買通行證",
    "ws.credits.nudgeTitle": "您的稽核額度已用盡——購買單次通行證即可再執行一次信用狀檢查",
    "ws.credits.monthly": "本月審單：{used}/{limit}",
    "ws.credits.monthlyTitle": "本計費週期內已使用的託管 AI 審單次數",
    "ws.credits.limitReached": "{used}/{limit} · 升級方案",
    "ws.credits.limitTitle": "您已達本月託管審單上限。購買單次通行證或升級方案以繼續。",
    "ws.managedMetered": "託管 {model}——本月尚餘 {n} / {limit} 次審單。",
    "ws.managedSolo": "終身版——請切換至自備金鑰（BYO-Key）模式，使用您自己的 Anthropic API 金鑰執行無限次審單。",
    "ws.freeBanner.used": "您已使用 1 次免費額度 — 升級方案以繼續",
    "ws.freeBanner.upgrade": "升級方案",
    "priv.title": "本地隱私",
    "priv.mask.all": "一鍵全部遮蔽",
    "priv.autoMask": "貼上時自動遮蔽敏感資料",
    "priv.reportBadge": "🔒 已於用戶端遮蔽輸入後審核（隱私優先）",
    "priv.redactedCount": "🔒 已安全遮蔽 {n} 處敏感數據",
    "ex.redactedBadge": "已於用戶端遮蔽輸入後審核（隱私優先）",
    "priv.mask.amounts": "遮蔽所有金額",
    "priv.mask.parties": "遮蔽當事人",
    "priv.mask.accounts": "遮蔽帳號/IBAN",
    "priv.reset": "還原原始文字",
    "priv.tip": "遮蔽金額或當事人可能降低金額、寬容度與當事人檢查的準確度。LC Shield 為預先檢查輔助工具，不能取代您自身的覆核。",
    "priv.toast.masked": "敏感資料已於瀏覽器本地遮蔽。",
    "priv.toast.reset": "已從本地快照還原原始文字。",
    "shield.title": "隱私優先設計",
    "shield.wsTitle": "記憶體內處理",
    "shield.b1t": "用戶端本地遮蔽",
    "shield.b1d": "提交前於瀏覽器內遮蔽金額、當事人與帳號。",
    "shield.b2t": "隔離記憶體內審單",
    "shield.b2d": "於隔離記憶體中處理；上傳的單據於分析後不會保留。",
    "shield.b3t": "零 AI 模型訓練",
    "shield.b3d": "您的貿易資料於分析後不會儲存於我們的伺服器，亦不用於訓練 LLM 模型。Firm 方案稽核歷史僅在您選擇啟用時才會儲存。",
    "ent.mnda": "需要客製化合規保證？Firm 與 Enterprise 方案可依需求提供雙向保密協議（MNDA）。",
    "ws.toast.cleared": "工作區已清空。",
    "ws.toast.needSwift": "請先貼上 SWIFT MT700 訊息。",
    "ws.toast.mapped": "MT700 已對應為信用狀條款。",
    "ws.toast.histLoaded": "已從歷史紀錄載入。",
    "ws.toast.needLc": "請新增信用狀條款。",
    "ws.toast.needDoc": "請至少新增一份單據。",
    "ws.toast.analysisDone": "分析完成。",
    "ws.errMap": "無法對應此 MT700 訊息。",
    "ws.errAmendment": "無法草擬修改函。",
    "ws.errAnalyze": "分析失敗，請重試。",
    "md.amendTitle": "信用狀修改申請書",
    "md.amendDrafting": "正在草擬修改函…",
    "md.amendReqChanges": "要求變更事項",
    "md.amendFrom": "原為：",
    "md.amendTo": "改為：",
    "md.copy": "複製",
    "md.download": "下載 .txt",
    "md.copyFull": "複製全文",
    "md.downloadTxt": "下載 .txt",
    "md.downloadPdf": "下載 .pdf",
    "md.amendUcpRef": "UCP 600 條文",
    "md.amendDisclaimer": "免責聲明：本修改申請書為 LC Shield 自動化助理依據國際商會 UCP 600 及 ISBP 821 (2023) 規則所草擬之草稿，不具法律約束力，亦不構成法律意見，申請人於提交開狀銀行前須自行審閱並授權。",
    "md.amendPdfDone": "PDF 已下載。",
    "md.amendTxtDone": "文字檔已下載。",
    "md.amendExportFailed": "匯出失敗 — 請再試一次。",
    "md.amendSaved": "已儲存",
    "md.amendSavedEmpty": "尚無已儲存的修改函。您產生的修改函會自動儲存於此。",
    "md.amendBackCurrent": "返回目前草稿",
    "md.bankSection": "收件人 — 開狀銀行",
    "md.bankNamePh": "開狀銀行名稱",
    "md.bankEmailPh": "銀行電郵（用於電郵草稿）",
    "md.bankAddressPh": "開狀銀行地址",
    "md.bankHint": "儲存於本裝置，並會加入每份匯出檔案與電郵草稿的信件開頭。",
    "md.openEmail": "以電郵開啟",
    "md.amendEmailOpened": "正在開啟您的電郵程式…",
    "md.amendCopied": "修改函已複製到剪貼簿。",
    "md.copyFailed": "複製失敗 — 請手動選取並複製文字。",
    "md.histEmpty1": "尚無已儲存的檢查。",
    "md.histEmpty2": "您的分析會自動儲存於此。",
    "md.histScore": "評分 {score}/100 · {crit} 項嚴重 · {docs} 份單據",
    "md.reload": "重新載入",
    "md.delete": "刪除",
    "md.hiwTitle": "一分鐘快速上手",
    "md.hiwIntro": "三步備妥可交單單據：",
    "md.hiwStep": "步驟",
    "md.hiwFlow": [
      { "title": "貼上 MT700", "body": "放入您的 SWIFT MT700 信用狀，或上傳草稿 PDF 單據。" },
      { "title": "執行 AI 檢查", "body": "引擎將每個欄位與 UCP 600 及 ISBP 821 (2023) 逐項比對。" },
      { "title": "下載", "body": "匯出可交銀行的 PDF 報告，或自動草擬修改函。" }
    ],
    "md.hiwByoTitle": "60 秒設定自帶金鑰",
    "md.hiwByoIntro": "單人版（自帶金鑰）使用者可自帶金鑰進行無限次的私密檢查。於此取得金鑰：",
    "md.hiwNoteAnthropic": "Console → API Keys → Create Key，複製 sk-ant-… 值。",
    "md.hiwByoOutro": "將金鑰貼入「引擎 → 自帶金鑰」欄位，並點擊「測試連線」。",
    "md.hiwDisclaimer": "LC Shield 是依 ICC UCP 600 與 ISBP 821 (2023) 的自動化單據查驗助手。輸出結果不具約束力，亦非法律、銀行或財務建議——請務必與您的銀行確認。",
    "md.hiwStartTour": "🚀 啟動手把手實操引導",
    "tour.skip": "跳過引導",
    "tour.prev": "上一步",
    "tour.next": "下一步",
    "tour.finish": "完成",
    "tour.step": "第 {n} / {total} 步",
    "tour.clickHint": "請點擊上方標示的按鈕以繼續",
    "tour.waiting": "正在等待標示的步驟出現…",
    "tour.sampleLoaded": "已載入範例——請檢視下方的發現。",
    "tour.steps": [
      { "title": "AI 審單引擎", "body": "選擇示範模式或輸入自備 API Key，隨時切換 AI 審單引擎。您有 1 次免費真實信用狀審單額度可立即使用！" },
      { "title": "隱私優先 — 本地遮蔽", "body": "隱私優先：您可在審單前一鍵遮蔽敏感金額或客戶名稱，LC Shield 依然能精準比對 UCP 600 條款不符點。" },
      { "title": "載入真實範例", "body": "點擊這裡！自動載入一張真實的出口信用狀與提單範例——範例永遠免費，不會消耗您的免費額度。" },
      { "title": "啟動 AI 審單", "body": "點擊這裡，讓 AI 秒級比對 UCP 600 條款與單據不符點。" },
      { "title": "不符點結果", "body": "查看 AI 精準標示出的退單風險與修改建議。" },
      { "title": "產生修改申請書", "body": "點擊這裡！為您的銀行或買家草擬修改申請書。" }
    ],
    "md.obSteps": [
      { "title": "無帳號與隱私機制", "body": "無需繁瑣密碼：採用隱私優先架構——您的單據於分析後不會儲存於我們的伺服器。您的訂閱權限將自動綁定 Stripe 結帳 Email，換設備時只需點擊郵件驗證連結即可秒速復原。" },
      { "title": "載入產業範本", "body": "可任意切換紡織、電子或大宗商品三大產業範本，通常 30 秒內體驗真實信用狀預審。" },
      { "title": "高階報告匯出", "body": "一鍵生成含 UCP 600 國際法規對照的高清 PDF 與 CSV 報告，直接提供給銀行或工廠溝通。" }
    ],
    "md.obDontShow": "不再顯示",
    "md.obBack": "上一步",
    "md.obNext": "下一步",
    "md.obStart": "免費試用一次稽核",
    "ex.title": "交單就緒報告",
    "ex.generated": "產生時間 {date}  ·  參考編號 {ref}  ·  引擎 {model}",
    "ex.ready": "可供交單",
    "ex.notReady": "尚未就緒——發現不符點",
    "ex.score": "交單就緒評分",
    "ex.critical": "嚴重",
    "ex.warnings": "警告",
    "ex.notes": "備註",
    "ex.discrepancies": "不符點",
    "ex.document": "單據",
    "ex.reference": "條文引用",
    "ex.suggestedFix": "建議修正",
    "ex.disclaimer": "免責聲明：LC Shield 是依 ICC UCP 600 與 ISBP 821 (2023) 規則運作的自動化單據查驗助手，提供不具約束力的作業協助，並不構成法律意見、銀行擔保或開狀行的正式單據接受。",
    "ex.csvHeaders": ["嚴重程度", "標題", "單據", "信用狀要求", "單據內容", "UCP 條文", "建議修正"],
    "ex.preparing": "正在準備中文 PDF…",
    "ws.title": "工作區",
    "ws.engine": "引擎",
    "ws.demo": "示範（代管）",
    "ws.byo": "自帶金鑰",
    "ws.lcTerms": "信用狀條款",
    "ws.loadSample": "載入範例",
    "team.title": "企業團隊席位",
    "team.sub": "企業方案最多包含 8 個席位。分享同一個邀請連結，團隊成員即可立即解鎖完整權限，無額外費用。",
    "team.ownerEmail": "您的企業帳號 Email",
    "team.create": "建立 / 載入團隊",
    "team.inviteLink": "團隊邀請連結",
    "team.copy": "複製連結",
    "team.members": "成員",
    "team.seatsUsed": "席位已使用",
    "ws.loadScenario": "載入範例情境",
    "ws.scn.textiles": "紡織與成衣",
    "ws.scn.textilesSub": "高風險 · 缺件、逾期裝運、金額超限",
    "ws.scn.electronics": "電子與機械",
    "ws.scn.electronicsSub": "乾淨交單 · 完全相符文件",
    "ws.scn.commodities": "大宗商品與農產",
    "ws.scn.commoditiesSub": "多單據 · 產地與匯票不符",
    "ws.pasteManual": "手動 / 貼上",
    "ws.pasteSwift": "貼上 SWIFT MT700",
    "ws.mapSwift": "解析 MT700 欄位",
    "ws.documents": "已提交單據",
    "ws.addDoc": "新增單據",
    "ws.upload": "上傳",
    "ws.run": "執行不符點檢查",
    "ws.running": "正在檢查交單…",
    "ws.report": "不符點報告",
    "ws.empty": "尚未檢查任何交單",
    "ws.emptySub": "載入信用狀與單據後執行不符點檢查，結果將依嚴重程度排列於此。",
    "ws.ready": "可交單",
    "ws.notReady": "尚未就緒",
    "ws.history": "歷史紀錄",
    "ws.letters": "修改函",
    "ws.amendment": "產生修改申請書",
    "ws.keyVerified": "金鑰已驗證",
    "ws.keyNotSet": "未設定金鑰",
    "ws.demoEngine": "示範引擎",
    "ws.unlocked": "已解鎖",
    "ws.privacyShield": "隱私防護",
    "ws.team": "團隊",
    "ws.howItWorks": "運作方式",
    "ws.quickTour": "操作引導",
    "chat.title": "Shield 智能助理",
    "chat.subtitle": "詢問任何關於 LC Shield 的問題",
    "chat.greeting": "您好！我是 Shield 智能助理 🛡️ 歡迎詢問範例試用、隱私機制或方案價格，也可點擊下方建議問題。",
    "chat.placeholder": "輸入您的問題…",
    "chat.thinking": "思考中…",
    "chat.pill0": "如何試用 MT700 範例？",
    "chat.pill1": "我的貿易資料如何處理？",
    "chat.pill2": "價格與方案",
    "chat.emailBtn": "聯絡我們團隊",
    "chat.open": "開啟客服對話",
    "chat.error": "抱歉，暫時無法回覆，請來信 support@getlcshield.com。",
    "ws.testConnection": "測試連線",
    "privacy.badge": "全球隱私標準：記憶體內處理——您的單據於分析後不會儲存於我們的伺服器。部分設定與歷史可能儲存於您的瀏覽器。",
    "disclaimer.global": "免責聲明：LC Shield 是依據 ICC UCP 600 與 ISBP 821 (2023) 規則運作的自動化單據核對助手，僅提供非約束性的作業協助，並不構成法律意見、銀行保證或開狀行對單據的正式承兌。",
    "paywall.title": "您的免費檢查已用完",
    "paywall.freeTitle": "保護您的下一次信用狀交單，免受銀行費用——每次拒付通常 USD 50-150",
    "paywall.freeSub": "選擇 1 次單次通行證處理緊急稽核，或升級專業版以獲得最大成本節省。",
    "paywall.trustLine": "零設定 • 隨時取消 • 嚴謹資料保密",
    "paywall.recommended": "推薦",
    "paywall.cta.single": "購買單次通行證",
    "paywall.cta.pro": "訂閱專業版",
    "paywall.saveCopy": "選專業版，每次稽核可省 55%+。",
    "paywall.moreOptions": "更多方案",
    "paywall.currency": "貨幣",
    "paywall.forByo": "自備金鑰首選",
    "paywall.sub": "解鎖 SWIFT MT700 解析與修改申請書草擬——或自帶 Anthropic 金鑰免費使用。",
    "paywall.viewPlans": "查看方案",
    "paywall.byo": "使用我的 API 金鑰",
    "paywall.already": "已購買？以電郵解鎖",
    "paywall.unlock": "解鎖",
    "paywall.emailPh": "結帳時使用的電郵",
  },
  "zh-CN": {
    "nav.how": "运作方式",
    "nav.engine": "引擎",
    "nav.pricing": "价格",
    "nav.resources": "资源中心",
    "nav.open": "打开工作区",
    "hero.overline": "UCP 600 · 全球跟单信用证助手",
    "hero.title1": "在数秒内揪出信用证不符点，",
    "hero.title2": "抢在银行之前。",
    "hero.demo.tag": "实时不符点扫描",
    "guarantee.badge": "审核费用保证",
    "guarantee.body": "若 LC Shield 漏判某项不符点，而您的银行于拒付通知中引用该不符点，且您在拒付后 30 天内将该通知寄给我们，我们将退还您就该次交单所支付的审核费用。",
    "guarantee.micro": "免费审核与示例报告不涉及审核费用，不在退款范围内。",
    "guarantee.readTerms": "查看保证条款",
    "ws.sampleDownload": "下载示例行政级 PDF 审核报告",
    "hero.sub": "通常不到 30 秒——为全球出口商、物流代理与贸易顾问提供自动化 UCP 600 与 ISBP 821 (2023) 预先审核，在向开证行交单前标示隐藏的不符点陷阱。",
    "hero.cta": "免费检查一次交单",
    "hero.pricing": "查看价格",
    "hero.ctaPrimary": "免费试用一次审核",
    "hero.ctaSample": "查看审核样例",
    "hero.noCard": "无需注册账号或信用卡 • 免费试用一次审核",
    "hero.badge1": "内存内处理 · 自带密钥",
    "hero.badge2": "UCP 600 与 ISBP 821 (2023)",
    "hero.badge3": "一键 PDF / CSV",
    "pricing.overline": "价格",
    "pricing.title": "一份干净的单据即回本",
    "pricing.sub": "",
    "pricing.billedUsd": "以美元结算",
    "pricing.buyLifetime": "购买终身版",
    "pricing.subscribe": "订阅",
    "pricing.perMonth": "月",
    "pricing.footnote": "购买前可先试用工作区。",
    "work.overline": "运作流程",
    "work.title": "三步骤备妥可交单单据",
    "work.s1t": "1 · 载入信用证与单据",
    "work.s1d": "粘贴信用证条款，放入您的发票、提单、装箱单与保险单——或上传 PDF。上传的单据于分析后不会保留。",
    "work.s2t": "2 · 运行不符点检查",
    "work.s2d": "引擎将每个字段与信用证及 UCP 600 惯例逐项比对，并为交单就绪程度评分。",
    "work.s3t": "3 · 修正后导出",
    "work.s3d": "获取按严重程度排序的不符点清单，附 UCP 条文引用与建议修正，并可导出高管级 PDF 或 CSV 存档。",
    "stat.v1": "的首次交单被拒，ICC 估计（ICC 技术咨询简报第 3 号，2022）",
    "stat.v2": "常见的银行不符点费用——每次交单、每个周期",
    "stat.v3": "当单据被退回修正时的付款延误",
    "feat.overline": "零阻力引擎",
    "feat.title": "为承担不起退单的团队而打造",
    "feat.c1t": "UCP 600 不符点引擎",
    "feat.c1d": "金额与宽容度、货物描述、最迟装运与到期日、交单期限、正本与副本、背书、分批装运与转运规则——每项发现均引用相关条文。",
    "feat.c2t": "注重隐私的设计",
    "feat.c2d": "自带 Anthropic 密钥。上传的单据于分析后不会保留。Firm 方案审核历史与您的本机浏览器存储可能保存审核结果，但绝不保存单据文件本身。",
    "feat.c3t": "交单就绪评分",
    "feat.c3d": "单一 0–100 信心评分，并附实时代币成本估算，让您在运行前即掌握花费。",
    "feat.c4t": "为全球跨境贸易而建",
    "feat.c4d": "专为全球出口中小企业、货运代理与贸易柜台打造，处理跨司法管辖区与多币别的跟单信用证。",
    "sbx.overline": "即时沙盒",
    "sbx.title": "数秒内看到真实的不符点扫描",
    "sbx.sub": "无需注册、无需上传。以示例 SWIFT MT700 信用证运行我们的引擎，立即预览结果。",
    "sbx.btn": "以示例 MT700 信用证试用（点击测试）",
    "sbx.scanning": "正在比对 UCP 600 规则…",
    "sbx.steps": ["金额与宽容度", "装运与到期日", "保险与背书"],
    "sbx.header": "尚未就绪——发现 6 个不符点",
    "sbx.readiness": "就绪度 18 / 100",
    "sbx.critical": "严重",
    "sbx.lcReq": "信用证要求：",
    "sbx.docShows": "单据显示：",
    "sbx.revealed": [
      { "title": "信用证金额超出上限", "doc": "商业发票", "lc": "USD 50,000.00（宽容度 +/- 5% · 上限 USD 52,500）", "val": "USD 54,000.00", "ref": "LC field 39A; UCP 600 Art. 18(b)" },
      { "title": "逾越最迟装运日", "doc": "海运提单", "lc": "须于 2027-01-15（含）前装船", "val": "装船日 2027-01-20", "ref": "LC field 44C; UCP 600 Art. 20(a)(ii)" }
    ],
    "sbx.locked": ["未提供保险证明（46A）", "未提供装箱单（46A）", "发票：3 份正本仅提交 2 份", "发票货描漏列“100%”（45A）"],
    "sbx.unlock": "解锁其余 4 项风险发现与修改函",
    "sbx.startTrial": "解锁完整报告",
    "roi.overline": "试算数字",
    "roi.title": "一次退单究竟让您付出多少代价",
    "roi.lcsLabel": "每月处理的信用证数",
    "roi.valLabel": "平均信用证金额",
    "roi.riskLabel": "预估每年可避免的不符点风险",
    "roi.hoursLabel": "每月节省的工时",
    "roi.hrs": "小时",
    "roi.planLabel": "LC Shield 专业版方案",
    "roi.roiLabel": "预估投资回报率",
    "roi.cta": "立即开始守护这些数字",
    "roi.perMo": "/月",
    "roi.note1": "每月信用证数与平均信用证金额可自行调整；其余数字仅供说明（非 ICC 数据）：首次交单不符率、常见银行费用 ",
    "roi.note2": "（一般为 USD 50-150），以及 1.5% 的资金延迟成本。并非保证。",
    "cmp.overline": "为何转换",
    "cmp.title": "全球出口商为何从人工核对转向 LC Shield",
    "cmp.hDim": "维度",
    "cmp.hManual": "人工／银行内部审核",
    "cmp.hShield": "LC Shield AI 防护",
    "cmp.rows": [
      { "dim": "处理时间", "manual": "4 至 24 小时", "shield": "通常不到 30 秒" },
      { "dim": "UCP 600 / ISBP 条文对应", "manual": "需资深贸易专员", "shield": "即时自动查找条文" },
      { "dim": "解决方案输出", "manual": "人工草拟", "shield": "修改申请书草拟" },
      { "dim": "每份信用证审核成本", "manual": "内部人力成本", "shield": "单次稽核 US$15 起；专业版与企业版每次更低" }
    ],
    "plan.single.name": "单次审核通行证",
    "plan.single.badge": "按次付费",
    "plan.single.tag": "为紧急信用证交单提供的一次性检查。零设置、免订阅。",
    "plan.single.quota": "1 次完整信用证审核",
    "plan.single.cta": "购买单次通行证",
    "plan.single.features": [
      "1 次完整 UCP 600 与 ISBP 821 (2023) 不符点扫描",
      "SWIFT MT700 自动映射 + 修改函起草",
      "专业 PDF + CSV 导出",
      "内置 Claude Sonnet AI — 零设置",
    ],
    "plan.professional.name": "专业版",
    "plan.professional.badge": "单一出口商",
    "plan.professional.tag": "内置托管 AI 引擎 — 无需任何 API 设置。",
    "plan.professional.quota": "每月 30 次完整审核",
    "plan.professional.quotaNote": "单买约值 US$450",
    "plan.professional.cta": "订阅专业版",
    "plan.professional.features": [
      "每月 30 次托管信用证交单检查",
      "完整 UCP 600 / ISBP 821 (2023) 不符点引擎",
      "SWIFT MT700 自动映射 + 修改函起草",
      "专业 PDF + CSV 导出",
      "优先电子邮件支持",
    ],
    "plan.firm.name": "企业／代理版",
    "plan.firm.badge": "团队协作",
    "plan.firm.tag": "专为多队员协作与大批量单据审核而打造。",
    "plan.firm.quota": "每月 100 次共享审核",
    "plan.firm.quotaNote": "最多 8 个团队席位",
    "plan.firm.cta": "订阅企业版",
    "plan.firm.features": [
      "每月 100 次共享托管交单检查",
      "最多 8 个团队席位（共享团队授权）",
      "共享团队不符点记录与审核日志",
      "批量单据处理",
      "优先电子邮件支持与团队上线",
    ],
    "plan.solo.name": "终身单人版",
    "plan.solo.badge": "终身使用权",
    "plan.solo.tag": "一次买断，永久使用界面。API Token 费用由您直接支付给 Anthropic。",
    "plan.solo.quota": "无限次审核（自备 Anthropic API Key）",
    "plan.solo.cta": "购买终身版",
    "plan.solo.features": [
      "无限次交单 — 自备 Anthropic API Key",
      "完整 UCP 600 / ISBP 821 (2023) 不符点引擎",
      "SWIFT MT700 自动映射 + 修改函起草",
      "专业 PDF + CSV 导出",
      "终身功能更新",
    ],
    "pricing.quotaLabel": "包含",
    "pricing.starting": "启动中…",
    "pricing.oneTime": "一次性",
    "pricing.agreePrefix": "完成付款即表示您接受我们的",
    "agree.terms": "服务条款",
    "agree.and": "与",
    "agree.privacy": "隐私政策",
    "foot.terms": "服务条款",
    "foot.resources": "资源中心",
    "foot.privacy": "隐私政策",
    "foot.about": "关于我们",
    "foot.contact": "联系",
    "foot.security": "安全",
    "foot.copy": "LC Shield（DZent Limited）。版权所有。",
    "mobile.startScan": "免费试用一次审核",
    "ws.workspace": "工作区",
    "ws.zeroStorage": "内存内处理",
    "ws.perRun": "/ 次",
    "ws.testing": "测试中…",
    "ws.mapping": "对应中…",
    "ws.upload": "上传",
    "ws.sampleMt700": "示例 MT700",
    "ws.byoNote": "您的密钥仅用于此次请求，不会存储于我们的服务器。模型：",
    "apikey.trigger": "API Key 设置指引（30秒）→",
    "apikey.howtoLink": "[ 如何获取？ ]",
    "apikey.title": "如何获取您的 Anthropic API Key？",
    "apikey.stepLabel": "步骤",
    "apikey.step1": "访问 Anthropic 官网 (console.anthropic.com) 并完成免费注册。",
    "apikey.step2": "进入 API Keys 页面，点击“Create Key”并复制您的专属密钥。",
    "apikey.step3": "粘贴至 LC Shield 设置面板，即可享受无上限 AI 信用状预审！",
    "apikey.cost": "通过自备 Key，每份信用状预审的 Anthropic 实际 API 消耗低至 ~$0.02 USD (约 $0.16 HKD)，成本极低且完全由您掌控。",
    "pay.cancelTitle": "付款已取消",
    "pay.cancelSub": "系统未进行任何扣款。您仍可在示范模式中免费体验工作区。",
    "pay.pricingBtn": "查看方案",
    "pay.workspaceBtn": "前往工作区",
    "ws.managedUnlocked": "已解锁 - 在 {model} 上进行托管分析（适用方案额度）。",
    "ws.managedRunning": "正在使用托管的 {model} 引擎。{free}",
    "ws.freeUsed": "免费检查已用完——请解锁以继续。",
    "ws.freeIncluded": "包含 {n} 次免费检查。",
    "ws.wsAgree": "运行检查即表示您同意我们的",
    "ws.loadingTitle": "正在比对 UCP 600…",
    "ws.loadSteps": ["金额与宽容度", "装运与到期日", "货物描述", "正本与背书"],
    "ws.lcRequires": "信用证要求：",
    "ws.docShows": "单据显示：",
    "ws.fix": "修正：",
    "ws.checksPassed": "已通过的检查",
    "ws.sev.critical": "严重",
    "ws.sev.warning": "警告",
    "ws.sev.info": "备注",
    "ws.phKey": "sk-ant-… 或 sk-…（您的密钥，不会存储于我们的服务器）",
    "ws.phSwift": ":20: 信用证号…  :31D: 到期…  :32B: 币别／金额…  :44C: 最迟装运…  :45A: 货物…  :46A: 所需单据…",
    "ws.phManual": "粘贴跟单信用证条款：金额与宽容度、货物、贸易条件、最迟装运与到期日、交单期限、所需单据…",
    "ws.phDoc": "粘贴单据文字，或于上方上传 PDF／文字文件…",
    "ws.docTypeMap": {
      "Commercial Invoice": "商业发票",
      "Bill of Lading / Transport Doc": "提单／运输单据",
      "Packing List": "装箱单",
      "Insurance Certificate": "保险证明",
      "Certificate of Origin": "原产地证明",
      "Bill of Exchange / Draft": "汇票／汇券",
      "Other Document": "其他单据"
    },
    "ws.toast.invite": "已应用团队邀请。",
    "ws.toast.demoScan": "正在运行您的示例 MT700 扫描…",
    "ws.toast.singleCredit": "付款成功 — 已新增 1 次审核额度，现在即可运行信用证检查。",
    "ws.toast.paid": "🎉 付款已验证！欢迎使用 LC Shield，您的账户已解锁。",
    "ws.toast.needKey": "请输入您的 Anthropic 密钥，或切换至体验引擎。",
    "ws.toast.pasteKey": "请先粘贴您的 API 密钥。",
    "ws.toast.connOk": "🟢 连接成功！已可进行无限次检查。",
    "ws.toast.connBad": "🔴 密钥无效或额度已用尽，请检查您的密钥。",
    "ws.toast.loadedChars": "已从 {file} 载入 {n} 个字符",
    "ws.toast.readFail": "无法读取该文件。",
    "ws.toast.sampleLoaded": "已载入示例信用证与单据。",
    "ws.clearAll": "清空内容",
    "ws.freeBanner.remaining": "剩余 1 次免费真实审单额度",
    "ws.credits.left": "额度：{n}",
    "ws.credits.unlimited": "额度：无限",
    "ws.credits.title": "您剩余的信用证审核额度（免费 + 已购买的单次通行证额度）",
    "ws.credits.nudge": "0 额度 · 购买通行证",
    "ws.credits.nudgeTitle": "您的审核额度已用尽——购买单次通行证即可再运行一次信用证检查",
    "ws.credits.monthly": "本月审单：{used}/{limit}",
    "ws.credits.monthlyTitle": "本计费周期内已使用的托管 AI 审单次数",
    "ws.credits.limitReached": "{used}/{limit} · 升级方案",
    "ws.credits.limitTitle": "您已达本月托管审单上限。购买单次通行证或升级方案以继续。",
    "ws.managedMetered": "托管 {model}——本月尚余 {n} / {limit} 次审单。",
    "ws.managedSolo": "终身版——请切换至自备密钥（BYO-Key）模式，使用您自己的 Anthropic API 密钥运行无限次审单。",
    "ws.freeBanner.used": "您已使用 1 次免费额度 — 升级方案以继续",
    "ws.freeBanner.upgrade": "升级方案",
    "priv.title": "本地隐私",
    "priv.mask.all": "一键全部遮蔽",
    "priv.autoMask": "粘贴时自动遮蔽敏感资料",
    "priv.reportBadge": "🔒 已于客户端遮蔽输入后审核（隐私优先）",
    "priv.redactedCount": "🔒 已安全遮蔽 {n} 处敏感数据",
    "ex.redactedBadge": "已于客户端遮蔽输入后审核（隐私优先）",
    "priv.mask.amounts": "遮蔽所有金额",
    "priv.mask.parties": "遮蔽当事人",
    "priv.mask.accounts": "遮蔽账号/IBAN",
    "priv.reset": "还原原始文字",
    "priv.tip": "遮蔽金额或当事人可能降低金额、宽容度与当事人检查的准确度。LC Shield 为预先检查辅助工具，不能取代您自身的复核。",
    "priv.toast.masked": "敏感资料已于浏览器本地遮蔽。",
    "priv.toast.reset": "已从本地快照还原原始文字。",
    "shield.title": "隐私优先设计",
    "shield.wsTitle": "内存内处理",
    "shield.b1t": "客户端本地遮蔽",
    "shield.b1d": "提交前于浏览器内遮蔽金额、当事人与账号。",
    "shield.b2t": "隔离内存内审单",
    "shield.b2d": "于隔离内存中处理；上传的单据于分析后不会保留。",
    "shield.b3t": "零 AI 模型训练",
    "shield.b3d": "您的贸易数据于分析后不会存储于我们的服务器，亦不用于训练 LLM 模型。Firm 方案审核历史仅在您选择启用时才会存储。",
    "ent.mnda": "需要定制化合规保证？Firm 与 Enterprise 方案可按需提供双向保密协议（MNDA）。",
    "ws.toast.cleared": "工作区已清空。",
    "ws.toast.needSwift": "请先粘贴 SWIFT MT700 报文。",
    "ws.toast.mapped": "MT700 已对应为信用证条款。",
    "ws.toast.histLoaded": "已从历史记录载入。",
    "ws.toast.needLc": "请添加信用证条款。",
    "ws.toast.needDoc": "请至少添加一份单据。",
    "ws.toast.analysisDone": "分析完成。",
    "ws.errMap": "无法对应此 MT700 报文。",
    "ws.errAmendment": "无法草拟修改函。",
    "ws.errAnalyze": "分析失败，请重试。",
    "md.amendTitle": "信用证修改申请书",
    "md.amendDrafting": "正在草拟修改函…",
    "md.amendReqChanges": "要求变更事项",
    "md.amendFrom": "原为：",
    "md.amendTo": "改为：",
    "md.copy": "复制",
    "md.download": "下载 .txt",
    "md.copyFull": "复制全文",
    "md.downloadTxt": "下载 .txt",
    "md.downloadPdf": "下载 .pdf",
    "md.amendUcpRef": "UCP 600 条文",
    "md.amendDisclaimer": "免责声明：本修改申请书为 LC Shield 自动化助理依据国际商会 UCP 600 及 ISBP 821 (2023) 规则所草拟之草稿，不具法律约束力，亦不构成法律意见，申请人于提交开证银行前须自行审阅并授权。",
    "md.amendPdfDone": "PDF 已下载。",
    "md.amendTxtDone": "文本文件已下载。",
    "md.amendExportFailed": "导出失败 — 请再试一次。",
    "md.amendSaved": "已保存",
    "md.amendSavedEmpty": "尚无已保存的修改函。您生成的修改函会自动保存于此。",
    "md.amendBackCurrent": "返回当前草稿",
    "md.bankSection": "收件人 — 开证银行",
    "md.bankNamePh": "开证银行名称",
    "md.bankEmailPh": "银行电邮（用于电邮草稿）",
    "md.bankAddressPh": "开证银行地址",
    "md.bankHint": "保存于本设备，并会加入每份导出文件与电邮草稿的信件开头。",
    "md.openEmail": "以电邮打开",
    "md.amendEmailOpened": "正在打开您的电邮程序…",
    "md.amendCopied": "修改函已复制到剪贴板。",
    "md.copyFailed": "复制失败 — 请手动选取并复制文字。",
    "md.histEmpty1": "尚无已保存的检查。",
    "md.histEmpty2": "您的分析会自动保存于此。",
    "md.histScore": "评分 {score}/100 · {crit} 项严重 · {docs} 份单据",
    "md.reload": "重新载入",
    "md.delete": "删除",
    "md.hiwTitle": "一分钟快速上手",
    "md.hiwIntro": "三步骤备妥可交单单据：",
    "md.hiwStep": "步骤",
    "md.hiwFlow": [
      { "title": "粘贴 MT700", "body": "放入您的 SWIFT MT700 信用证，或上传草稿 PDF 单据。" },
      { "title": "运行 AI 检查", "body": "引擎将每个字段与 UCP 600 及 ISBP 821 (2023) 逐项比对。" },
      { "title": "下载", "body": "导出可交银行的 PDF 报告，或自动草拟修改函。" }
    ],
    "md.hiwByoTitle": "60 秒设置自带密钥",
    "md.hiwByoIntro": "单人版（自带密钥）用户可自带密钥进行无限次的私密检查。于此获取密钥：",
    "md.hiwNoteAnthropic": "Console → API Keys → Create Key，复制 sk-ant-… 值。",
    "md.hiwByoOutro": "将密钥粘贴入“引擎 → 自带密钥”字段，并点击“测试连接”。",
    "md.hiwDisclaimer": "LC Shield 是依 ICC UCP 600 与 ISBP 821 (2023) 的自动化单据查验助手。输出结果不具约束力，亦非法律、银行或财务建议——请务必与您的银行确认。",
    "md.hiwStartTour": "🚀 启动手把手实操引导",
    "tour.skip": "跳过引导",
    "tour.prev": "上一步",
    "tour.next": "下一步",
    "tour.finish": "完成",
    "tour.step": "第 {n} / {total} 步",
    "tour.clickHint": "请点击上方标示的按钮以继续",
    "tour.waiting": "正在等待标示的步骤出现…",
    "tour.sampleLoaded": "已载入示例——请查看下方的发现。",
    "tour.steps": [
      { "title": "AI 审单引擎", "body": "选择示范模式或输入自备 API Key，随时切换 AI 审单引擎。您有 1 次免费真实信用证审单额度可立即使用！" },
      { "title": "隐私优先 — 本地遮蔽", "body": "隐私优先：您可在审单前一键遮蔽敏感金额或客户名称，LC Shield 依然能精准比对 UCP 600 条款不符点。" },
      { "title": "载入真实范例", "body": "点击这里！自动载入一张真实的出口信用证与提单范例——范例永远免费，不会消耗您的免费额度。" },
      { "title": "启动 AI 审单", "body": "点击这里，让 AI 秒级比对 UCP 600 条款与单据不符点。" },
      { "title": "不符点结果", "body": "查看 AI 精准标示出的退单风险与修改建议。" },
      { "title": "生成修改申请书", "body": "点击这里！为您的银行或买家草拟修改申请书。" }
    ],
    "md.obSteps": [
      { "title": "无账号与隐私机制", "body": "无需繁琐密码：采用隐私优先架构——您的单据于分析后不会存储于我们的服务器。您的订阅权限将自动绑定 Stripe 结账 Email，换设备时只需点击邮件验证链接即可秒速恢复。" },
      { "title": "载入行业范本", "body": "可任意切换纺织、电子或大宗商品三大行业范本，通常 30 秒内体验真实信用证预审。" },
      { "title": "高阶报告导出", "body": "一键生成含 UCP 600 国际法规对照的高清 PDF 与 CSV 报告，直接提供给银行或工厂沟通。" }
    ],
    "md.obDontShow": "不再显示",
    "md.obBack": "上一步",
    "md.obNext": "下一步",
    "md.obStart": "免费试用一次审核",
    "ex.title": "交单就绪报告",
    "ex.generated": "生成时间 {date}  ·  参考编号 {ref}  ·  引擎 {model}",
    "ex.ready": "可供交单",
    "ex.notReady": "尚未就绪——发现不符点",
    "ex.score": "交单就绪评分",
    "ex.critical": "严重",
    "ex.warnings": "警告",
    "ex.notes": "备注",
    "ex.discrepancies": "不符点",
    "ex.document": "单据",
    "ex.reference": "条文引用",
    "ex.suggestedFix": "建议修正",
    "ex.disclaimer": "免责声明：LC Shield 是依 ICC UCP 600 与 ISBP 821 (2023) 规则运作的自动化单据查验助手，提供不具约束力的作业协助，并不构成法律意见、银行担保或开证行的正式单据接受。",
    "ex.csvHeaders": ["严重程度", "标题", "单据", "信用证要求", "单据内容", "UCP 条文", "建议修正"],
    "ex.preparing": "正在准备中文 PDF…",
    "ws.title": "工作区",
    "ws.engine": "引擎",
    "ws.demo": "演示（托管）",
    "ws.byo": "自带密钥",
    "ws.lcTerms": "信用证条款",
    "ws.loadSample": "载入示例",
    "team.title": "企业团队席位",
    "team.sub": "企业方案最多包含 8 个席位。分享同一个邀请链接，团队成员即可立即解锁完整权限，无额外费用。",
    "team.ownerEmail": "您的企业账号 Email",
    "team.create": "创建 / 载入团队",
    "team.inviteLink": "团队邀请链接",
    "team.copy": "复制链接",
    "team.members": "成员",
    "team.seatsUsed": "席位已使用",
    "ws.loadScenario": "载入示例场景",
    "ws.scn.textiles": "纺织与成衣",
    "ws.scn.textilesSub": "高风险 · 缺件、逾期装运、金额超限",
    "ws.scn.electronics": "电子与机械",
    "ws.scn.electronicsSub": "干净交单 · 完全相符文件",
    "ws.scn.commodities": "大宗商品与农产",
    "ws.scn.commoditiesSub": "多单据 · 产地与汇票不符",
    "ws.pasteManual": "手动 / 粘贴",
    "ws.pasteSwift": "粘贴 SWIFT MT700",
    "ws.mapSwift": "解析 MT700 字段",
    "ws.documents": "已提交单据",
    "ws.addDoc": "添加单据",
    "ws.upload": "上传",
    "ws.run": "运行不符点检查",
    "ws.running": "正在检查交单…",
    "ws.report": "不符点报告",
    "ws.empty": "尚未检查任何交单",
    "ws.emptySub": "载入信用证与单据后运行不符点检查，结果将按严重程度排列于此。",
    "ws.ready": "可交单",
    "ws.notReady": "尚未就绪",
    "ws.history": "历史记录",
    "ws.letters": "修改函",
    "ws.amendment": "生成修改申请书",
    "ws.keyVerified": "密钥已验证",
    "ws.keyNotSet": "未设置密钥",
    "ws.demoEngine": "演示引擎",
    "ws.unlocked": "已解锁",
    "ws.privacyShield": "隐私防护",
    "ws.team": "团队",
    "ws.howItWorks": "运作方式",
    "ws.quickTour": "操作引导",
    "chat.title": "Shield 智能助手",
    "chat.subtitle": "询问任何关于 LC Shield 的问题",
    "chat.greeting": "您好！我是 Shield 智能助手 🛡️ 欢迎询问示例试用、隐私机制或方案价格，也可点击下方建议问题。",
    "chat.placeholder": "输入您的问题…",
    "chat.thinking": "思考中…",
    "chat.pill0": "如何试用 MT700 示例？",
    "chat.pill1": "我的贸易数据如何处理？",
    "chat.pill2": "价格与方案",
    "chat.emailBtn": "联系我们团队",
    "chat.open": "开启客服对话",
    "chat.error": "抱歉，暂时无法回复，请来信 support@getlcshield.com。",
    "ws.testConnection": "测试连接",
    "privacy.badge": "全球隐私标准：内存内处理——您的单据于分析后不会存储于我们的服务器。部分设置与历史可能存储于您的浏览器。",
    "disclaimer.global": "免责声明：LC Shield 是依据 ICC UCP 600 与 ISBP 821 (2023) 规则运作的自动化单据核对助手，仅提供非约束性的作业协助，并不构成法律意见、银行担保或开证行对单据的正式承兑。",
    "paywall.title": "您的免费检查已用完",
    "paywall.freeTitle": "保护您的下一次信用证交单，免受银行费用——每次拒付通常 USD 50-150",
    "paywall.freeSub": "选择 1 次单次通行证处理紧急审核，或升级专业版以获得最大成本节省。",
    "paywall.trustLine": "零设置 • 随时取消 • 严谨数据保密",
    "paywall.recommended": "推荐",
    "paywall.cta.single": "购买单次通行证",
    "paywall.cta.pro": "订阅专业版",
    "paywall.saveCopy": "选专业版，每次审核可省 55%+。",
    "paywall.moreOptions": "更多方案",
    "paywall.currency": "货币",
    "paywall.forByo": "自备密钥首选",
    "paywall.sub": "解锁 SWIFT MT700 解析与修改申请书草拟——或自带 Anthropic 密钥免费使用。",
    "paywall.viewPlans": "查看方案",
    "paywall.byo": "使用我自己的 API 密钥",
    "paywall.already": "已购买？用邮箱解锁",
    "paywall.unlock": "解锁",
    "paywall.emailPh": "结账时使用的邮箱",
  },
};

export const LANGS = [
  { code: "en", label: "EN" },
  { code: "zh-TW", label: "繁" },
  { code: "zh-CN", label: "简" },
];

const I18nContext = createContext(null);

const detectLang = () => {
  try {
    const q = (new URLSearchParams(window.location.search).get("lang") || "").trim().toLowerCase();
    if (q === "zh-tw" || q === "zh_tw" || q === "zh-hant") return "zh-TW";
    if (q === "zh-cn" || q === "zh_cn" || q === "zh-hans") return "zh-CN";
  } catch { /* no-op */ }
  return "en";
};

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);
  const setLang = useCallback((l) => {
    setLangState(l);
  }, []);
  const t = useCallback(
    (key) => (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key,
    [lang]
  );
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
